import difference from "lodash/difference";
import union from "lodash/union";
import { euNations } from "../../assets/collections/euNations";
import { ROOT_STORE_STORAGE_INDEX } from "../../constants/env";
import citizenship from "../../lib/questionnaire/citizenship";
import family from "../../lib/questionnaire/family";
import patrimonio from "../../lib/questionnaire/patrimonio";
import redditi from "../../lib/questionnaire/redditi";
import { QuestionnaireName } from "../enums/QuestionnaireName.enum";
import { getNextForm } from "../functions/formutil";
import { getCurrentStateFromLocalStorage } from "../utils";
import { AllActionsEnum, type AllActions } from "./actions";

interface IStepLike {
  stepname?: string;
}

interface ISectionLike {
  forms?: IStepLike[];
}

interface IFormLike {
  name?: string;
  sections?: ISectionLike[];
}

interface IComputedActiveStepMetadata {
  activeStepName: string | undefined;
  activeStepIndex: number;
  shouldClearSkipFlag: boolean;
}

export const defaultQuestionnaireForms = [citizenship, family, redditi, patrimonio] as const;

const DEFAULT_RESIDENCE_COUNTRY = "IT";
const DEFAULT_CITIZENSHIP_COUNTRY = [DEFAULT_RESIDENCE_COUNTRY];

const DEFAULT_CITIZENSHIP_SKIP_TARGET_STEP_NAME = "4";
const STEP_NAME_FOR_NOT_EU_NATIONS = "3_2";

function normalizeCitizenshipValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean) as string[];
  }
  if (typeof value === "string" && value) {
    return [value];
  }
  return [DEFAULT_RESIDENCE_COUNTRY];
}

function isOneOfEuNationsSelected(citizenshipValues: string[]): boolean {
  const euNationIds = euNations.map(function mapNationToId(nation) {
    return nation.id;
  });

  return citizenshipValues.some(function isSelectedInEu(selected) {
    return !!selected && euNationIds.includes(selected);
  });
}

function computeCitizenshipSkipTargetStepName(formValues: Record<string, unknown>): string {
  const rawCitizenship = formValues?.paesi_cittadinanza as unknown;
  const citizenshipValues = normalizeCitizenshipValues(rawCitizenship);

  const shouldGoToStepFour = isOneOfEuNationsSelected(citizenshipValues);
  if (shouldGoToStepFour) {
    return DEFAULT_CITIZENSHIP_SKIP_TARGET_STEP_NAME;
  }

  return STEP_NAME_FOR_NOT_EU_NATIONS;
}

function computeActiveStepMetadata(
  form: IFormLike,
  requestedActiveStepName: string | undefined,
  formValues: Record<string, unknown>
): IComputedActiveStepMetadata {
  const stepsArray = (form?.sections?.[0]?.forms as IStepLike[]) || [];

  const initialStepName = requestedActiveStepName || stepsArray?.[0]?.stepname;
  const initialIndex = stepsArray.findIndex(function findStepIndex(step: IStepLike) {
    return step?.stepname === initialStepName;
  });

  let computedActiveStepName = initialStepName;
  let computedActiveStepIndex = initialIndex >= 0 ? initialIndex : 0;
  let shouldClearSkipFlag = false;

  const shouldSkipInitialCitizenshipSteps = Boolean(formValues?.skip_citizenship_initial_steps);
  const isCitizenshipForm = form?.name === QuestionnaireName.Citizenship;
  const isExplicitNavigation = Boolean(requestedActiveStepName);

  if (isCitizenshipForm && shouldSkipInitialCitizenshipSteps && !isExplicitNavigation) {
    const skipTargetStepName = computeCitizenshipSkipTargetStepName(formValues);
    const targetIndex = stepsArray.findIndex(function findTargetIndex(step: IStepLike) {
      return step?.stepname === skipTargetStepName;
    });

    if (targetIndex >= 0) {
      computedActiveStepName = skipTargetStepName;
      computedActiveStepIndex = targetIndex;
      shouldClearSkipFlag = true;
    }
  }

  return {
    activeStepName: computedActiveStepName,
    activeStepIndex: computedActiveStepIndex,
    shouldClearSkipFlag,
  };
}

const initialState = {
  formValues: { guidedMode: true, Nome: null, applicant_PosizioneResidenza: null },
  stepHistory: [],
  changeSection: { section: "welcome", direction: null },
  currentForm: "welcome",
  activeStep: 0,
  activeStepName: defaultQuestionnaireForms[0].sections[0].forms[0].stepname,
  validationField: {},
  forms: defaultQuestionnaireForms,
  redirectOnEndSection: "",
  redirectOnFinishSection: "",
  keySaveState: ROOT_STORE_STORAGE_INDEX,
  questionnaireHasBeenDownloaded: false,
};

export default function updates(state = getCurrentStateFromLocalStorage(initialState), action: AllActions) {
  switch (action.type) {
    case AllActionsEnum.SET_CITIZEN_NAME:
      return { ...state, formValues: { ...state.formValues, Nome: action.payload.name } };
    case AllActionsEnum.SET_DEFAULT_FIRST_STEP_QUESTIONNAIRE_DATA:
      return {
        ...state,
        forms: defaultQuestionnaireForms,
        formValues: {
          ...state.formValues,
          CodiceFiscale: action.payload.taxCode,
          DataDiNascita: action.payload.birthDate,
          PrevDataDiNascita: action.payload.prevDataDiNascita,
          AttivitaPrincipale: action.payload.mainActivity,
          LuogoDiNascita: action.payload.birthPlace,
          SessoAnagrafico: action.payload.registrySex,
          paesi_cittadinanza: action.payload.citizenshipCountries,
          applicant_PosizioneResidenza: state.formValues.applicant_PosizioneResidenza
            ? state.formValues.applicant_PosizioneResidenza
            : action.payload.birthPlace,
        },
      };
    case AllActionsEnum.CHANGE_SECTION: {
      const form = getNextForm(state.forms, action.payload.section || state.currentForm, action.payload.direction);

      const computed = computeActiveStepMetadata(
        form as unknown as IFormLike,
        action.payload.activeStepName,
        state.formValues
      );
      const nextFormValues = computed.shouldClearSkipFlag
        ? { ...state.formValues, skip_citizenship_initial_steps: false }
        : state.formValues;

      return {
        ...state,
        formValues: nextFormValues,
        currentForm: form.name,
        activeStep: computed.activeStepIndex,
        stepHistory: [],
        activeStepName: computed.activeStepName,
      };
    }
    case AllActionsEnum.CHANGE_STEP: {
      return {
        ...state,
        activeStep: action.payload.activeStep,
      };
    }
    case AllActionsEnum.CHANGE_STEP_NAME: {
      return {
        ...state,
        activeStepName: action.payload.activeStepName,
      };
    }

    case AllActionsEnum.UPDATE_GUIDED_MODE: {
      return {
        ...state,
        formValues: { ...state.formValues, guidedMode: action.payload.value },
      };
    }

    case AllActionsEnum.UPDATE_FORM_FIELD_VALUE: {
      const prevFormValues = state.formValues;
      const forms = state.forms;

      const fieldName = action.payload.name;
      const fieldValue = action.payload.value;

      let newFormValues = prevFormValues;

      if (prevFormValues[fieldName] !== fieldValue) {
        const prevFormValuesKeys = Object.keys(prevFormValues || {});

        const allFormFields: Array<any> = [];
        const notDependentFields: Array<any> = [];

        forms.forEach((form: any) => {
          const sections = form.sections;
          const steps = sections[0].forms;

          steps.forEach((step: any) => {
            step.fields.forEach((field: any) => {
              allFormFields.push(field.name);
              if (!field.dependencies || !field.dependencies.includes(fieldName)) {
                notDependentFields.push(field.name);
              }
            });
          });
        });

        const diffPrevFields = difference(prevFormValuesKeys, allFormFields);
        const mergeBetweenNotToExcludeFieldsAndNotDepdendent = union(diffPrevFields, notDependentFields);

        newFormValues = {};

        mergeBetweenNotToExcludeFieldsAndNotDepdendent.forEach((x) => {
          if (prevFormValues[x] !== undefined) {
            newFormValues[x] = prevFormValues[x];
          }
        });

        //! repopulates with default
        forms.forEach((form: any) => {
          const sections = form.sections;
          const steps = sections[0].forms;

          steps.forEach((step: any) => {
            step.fields.forEach((field: any) => {
              if (
                (newFormValues[field.name] === undefined || newFormValues[field.name] === null) &&
                field.default !== undefined
              ) {
                newFormValues[field.name] = field.default;
              }
            });
          });
        });
      }

      return {
        ...state,
        formValues: { ...newFormValues, [fieldName]: fieldValue },
      };
    }
    case AllActionsEnum.RESTORE_SECTION: {
      return {
        ...state,
        changeSection: {
          section: state.changeSection.section,
          direction: null,
        },
      };
    }

    case AllActionsEnum.SET_FORM_FIELD_ERROR: {
      return {
        ...state,
        validationField: action.payload,
      };
    }

    case AllActionsEnum.RESET_FORM_FIELD_ERROR: {
      return {
        ...state,
        validationField: {},
      };
    }

    case AllActionsEnum.UPDATE_QUESTIONNAIRE_REDIRECT_OPTIONS: {
      return {
        ...state,
        redirectOnEndSection: action.payload.redirectOnEndSection,
        redirectOnFinishSection: action.payload.redirectOnFinishSection,
      };
    }

    case AllActionsEnum.UPDATE_QUESTIONNAIRE: {
      let tmpQuestionnaireHasBeenDownloaded = state.questionnaireHasBeenDownloaded;

      if (action.payload.notify === true) {
        tmpQuestionnaireHasBeenDownloaded = true;
      }

      const questionnaire = {
        ...state,
        ...action.payload.questionnaire,
        formValues: {
          ...state.formValues,
          ...(action.payload.questionnaire?.formValues || {}),
        },
        questionnaireHasBeenDownloaded: tmpQuestionnaireHasBeenDownloaded,
      };

      //! QUICK AND DIRTY 'paesi_cittadinanza' field with default value
      const citizenshipCountryFormValue =
        (questionnaire.formValues.paesi_cittadinanza || action.payload.questionnaire.paesi_cittadinanza) ??
        DEFAULT_CITIZENSHIP_COUNTRY;
      const isCitizenshipCountryFormValueAnArray = Array.isArray(citizenshipCountryFormValue);

      let citizenshipCountryFormValueComputed: string[] = [];
      if (isCitizenshipCountryFormValueAnArray) {
        citizenshipCountryFormValueComputed = citizenshipCountryFormValueComputed.concat(citizenshipCountryFormValue);
      } else {
        citizenshipCountryFormValueComputed.push(citizenshipCountryFormValue);
      }
      // END QUICK AND DIRTY SOLUTION

      //! QUICK AND DIRTY 'paese_residenza' field with default value
      const citizenshipResidenceFormValue =
        (questionnaire.formValues.paese_residenza || action.payload.questionnaire.paese_residenza) ??
        DEFAULT_RESIDENCE_COUNTRY;
      // END QUICK AND DIRTY SOLUTION

      const newFormValues = {
        ...questionnaire.formValues,
        applicant_PosizioneResidenza:
          questionnaire.formValues.applicant_PosizioneResidenza ||
          action.payload.questionnaire.applicant_PosizioneResidenza,
        paesi_cittadinanza: citizenshipCountryFormValueComputed,
        paese_residenza: citizenshipResidenceFormValue,
      };

      questionnaire.formValues = newFormValues;

      return { ...state, ...questionnaire };
    }
    case AllActionsEnum.RESET: {
      return { ...initialState };
    }
    case AllActionsEnum.SHOW_GUIDED_MODE: {
      return { ...state, formValues: { ...state.formValues, guidedMode: action.payload.guidedMode } };
    }
    case AllActionsEnum.SET_TAX_CODE_DATA: {
      return { ...state, formValues: { ...state.formValues, taxCodeData: action.payload.taxCodeData } };
    }
    case AllActionsEnum.RESET_INITIAL_STATE_FORMS_AND_SET_CURRENT: {
      return {
        ...state,
        forms: defaultQuestionnaireForms,
        currentForm: action.payload.currentForm,
      };
    }
    default:
      return { ...state };
  }
}
